import React, { useCallback, useEffect, useState } from "react";
import AiDiagnosisService from "../../../services/AiDiagnosisService";
import {
  getDateRangeForAuditFilter,
  getPaginationMeta,
  mapApiRowToAiAuditEntry,
} from "./visitAiAuditUtils";

const PER_PAGE_OPTIONS = [10, 25, 50, 100];

const VisitAiAuditModal = ({ isOpen, visitId, onClose, Toast }) => {
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("all-time");
  const [expandedItems, setExpandedItems] = useState({});
  const [expandedTextSections, setExpandedTextSections] = useState({});
  const [logEntries, setLogEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);

  const resetState = useCallback(() => {
    setStatusFilter("all");
    setDateFilter("all-time");
    setExpandedItems({});
    setExpandedTextSections({});
    setLogEntries([]);
    setLoading(false);
    setPage(1);
    setPerPage(25);
    setTotalPages(1);
    setTotalRecords(0);
  }, []);

  const fetchLogs = useCallback(async () => {
    if (!visitId) return;

    setLoading(true);
    try {
      const dateRangeQuery = getDateRangeForAuditFilter(dateFilter);
      const query = {
        page,
        per_page: perPage,
        visit_id: visitId,
        sort: "desc",
        ...(statusFilter && statusFilter !== "all" ? { status: statusFilter } : {}),
        ...(dateRangeQuery.date_from ? { date_from: dateRangeQuery.date_from } : {}),
        ...(dateRangeQuery.date_to ? { date_to: dateRangeQuery.date_to } : {}),
      };

      const response = await AiDiagnosisService.getLogs(query);
      const apiRows = response?.data || [];
      const pagination = response?.pagination || {};
      const mappedRows = apiRows.map((row, index) => mapApiRowToAiAuditEntry(row, index));

      setLogEntries(mappedRows);
      setExpandedItems({});
      setExpandedTextSections({});
      setTotalPages(Number(pagination?.last_page || 1));
      setTotalRecords(Number(pagination?.total ?? mappedRows.length ?? 0));
    } catch (error) {
      setLogEntries([]);
      setTotalPages(1);
      setTotalRecords(0);
      Toast?.error?.("Failed to load AI audit logs");
    } finally {
      setLoading(false);
    }
  }, [visitId, page, perPage, statusFilter, dateFilter, Toast]);

  useEffect(() => {
    if (isOpen && visitId) {
      fetchLogs();
    }
  }, [isOpen, visitId, fetchLogs]);

  useEffect(() => {
    if (!isOpen) {
      resetState();
    }
  }, [isOpen, resetState]);

  const handleClose = () => {
    onClose?.();
  };

  const toggleItem = (itemId) => {
    setExpandedItems((prev) => ({
      ...prev,
      [itemId]: !prev[itemId],
    }));
  };

  const toggleTextSection = (itemId, section) => {
    setExpandedTextSections((prev) => ({
      ...prev,
      [itemId]: {
        ...(prev[itemId] || {}),
        [section]: !(prev[itemId] || {})[section],
      },
    }));
  };

  const paginationNav =
    totalRecords > 0 ? getPaginationMeta(page, perPage, totalRecords, totalPages) : null;

  const handlePageNavigate = (nextPage) => {
    if (!paginationNav) return;
    const clamped = Math.min(Math.max(1, nextPage), paginationNav.totalPages);
    if (clamped === page) return;
    setPage(clamped);
  };

  const handlePerPageSelect = (value) => {
    if (value === perPage) return;
    setPerPage(value);
    setPage(1);
  };

  const handleStatusFilterChange = (value) => {
    setStatusFilter(value);
    setPage(1);
  };

  const handleDateFilterChange = (value) => {
    setDateFilter(value);
    setPage(1);
  };

  if (!isOpen || !visitId) {
    return null;
  }

  return (
    <>
      <div
        className="modal fade show"
        id="aiauditscreen"
        tabIndex="-1"
        aria-labelledby="aiauditscreenmodal"
        style={{ display: "block" }}
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-scrollable hResponsive modal-dialog-centered modal-xl">
          <div className="modal-content h-100">
            <div
              className="sh_modalBox d-flex flex-column h-100 overflow-hidden"
              style={{ backgroundColor: "#fff", minHeight: 0 }}
            >
              <div className="modal-header border-0 pb-0">
                <div className="d-flex flex-column">
                  <h5 className="modal-title font-weight-bold mb-0" id="aiauditscreenmodal">
                    AI Audit
                  </h5>
                </div>
                <button
                  type="button"
                  className="btn-close"
                  aria-label="Close"
                  onClick={handleClose}
                ></button>
              </div>
              <div className="modal-header d-flex flex-wrap align-items-center gap-3 pb-3">
                <div className="d-flex w-50 align-items-center gap-3">
                  <select
                    id="aiAuditStatusFilter"
                    className="form-control w-50 form-control-solid"
                    aria-label="Status filter"
                    value={statusFilter}
                    onChange={(e) => handleStatusFilterChange(e.target.value)}
                  >
                    <option value="all">All statuses</option>
                    <option value="success">Success</option>
                    <option value="error">Error</option>
                    <option value="pending">Pending</option>
                    <option value="processing">Processing</option>
                    <option value="failed">Failed</option>
                  </select>
                </div>
                <div className="">
                  <select
                    id="aiAuditDateFilter"
                    className="form-control form-control-solid"
                    aria-label="Date filter"
                    value={dateFilter}
                    onChange={(e) => handleDateFilterChange(e.target.value)}
                  >
                    <option value="today">Today</option>
                    <option value="yesterday">Yesterday</option>
                    <option value="last-7-days">Last 7 days</option>
                    <option value="last-30-days">Last 30 days</option>
                    <option value="all-time">All time</option>
                  </select>
                </div>
              </div>
              <div
                className="modal-body pt-0 overflow-auto flex-grow-1 position-relative"
                style={{ minHeight: 0 }}
              >
                <div className="sh_auditlog">
                  <div id="aiAuditTimeline" className="sh_auditlog-timeline">
                    {logEntries.map((entry) => {
                      const isItemOpen = !!expandedItems[entry.id];
                      const textSections = expandedTextSections[entry.id] || {};
                      const isInputTextOpen = !!textSections.input;
                      const isOutputTextOpen = !!textSections.output;
                      return (
                        <div
                          key={entry.id}
                          className={`sh_auditlog-item ${entry.itemClass || entry.actionType}`}
                          data-action={entry.actionType}
                          data-search={entry.dataSearch}
                        >
                          <div className="sh_auditlog-left">
                            <div className="sh_auditlog-time">{entry.time}</div>
                            <div className="sh_auditlog-date">{entry.date}</div>
                            <div className="sh_auditlog-relative">{entry.relative}</div>
                          </div>

                          <div className="sh_auditlog-card">
                            <a
                              className={`sh_auditlog-toggle${isItemOpen ? "" : " collapsed"}`}
                              type="button"
                              data-bs-toggle="collapse"
                              data-bs-target={`#${entry.id}`}
                              aria-expanded={isItemOpen}
                              aria-controls={entry.id}
                              onClick={(e) => {
                                e.preventDefault();
                                toggleItem(entry.id);
                              }}
                            >
                              <div className="d-flex justify-content-between sh_auditlog-toggle-container">
                                <div className="sh_auditlog-toggle-left">
                                  <div className="sh_auditlog-user">
                                    <i className="fa-solid fa-circle-user"></i>
                                    <span>{entry.user}</span>
                                  </div>
                                  <span className="badge rounded-pill sh_auditlog-badge">
                                    {entry.badgeIconClass ? (
                                      <i className={entry.badgeIconClass}></i>
                                    ) : null}{" "}
                                    {entry.badgeLabel}
                                  </span>
                                </div>
                                <div className="sh_auditlog-toggle-right">
                                  <span className="sh_auditlog-action">{entry.actionLabel}</span>
                                </div>
                              </div>
                            </a>

                            <div id={entry.id} className={`collapse${isItemOpen ? " show" : ""}`}>
                              <div className="sh_auditlog-body">
                                <div className="row g-2 mb-3">
                                  <div className="col-sm-6 col-md-3">
                                    <div className="small text-muted">Input tokens</div>
                                    <div className="fw-semibold">
                                      {entry.inputTokens != null ? entry.inputTokens : "—"}
                                    </div>
                                  </div>
                                  <div className="col-sm-6 col-md-3">
                                    <div className="small text-muted">Output tokens</div>
                                    <div className="fw-semibold">
                                      {entry.outputTokens != null ? entry.outputTokens : "—"}
                                    </div>
                                  </div>
                                  <div className="col-sm-6 col-md-3">
                                    <div className="small text-muted">Total tokens</div>
                                    <div className="fw-semibold">
                                      {entry.totalTokens != null
                                        ? entry.totalTokens
                                        : entry.inputTokens != null &&
                                          entry.outputTokens != null
                                        ? entry.inputTokens + entry.outputTokens
                                        : "—"}
                                    </div>
                                  </div>
                                  <div className="col-sm-6 col-md-3">
                                    <div className="small text-muted">Response time</div>
                                    <div className="fw-semibold">
                                      {entry.responseTimeMs != null
                                        ? `${entry.responseTimeMs} ms`
                                        : "—"}
                                    </div>
                                  </div>
                                </div>

                                {entry.errorMessage ? (
                                  <div className="alert alert-danger py-2 small mb-3">
                                    {entry.errorMessage}
                                  </div>
                                ) : null}

                                <div className="sh_auditlog-json-wrap">
                                  <div className="sh_auditlog-json-section">
                                    <div
                                      className="sh_auditlog-json-label sh_auditlog-json-old"
                                      role="button"
                                      tabIndex={0}
                                      style={{ cursor: "pointer" }}
                                      onClick={() => toggleTextSection(entry.id, "input")}
                                      onKeyDown={(e) => {
                                        if (e.key === "Enter" || e.key === " ") {
                                          e.preventDefault();
                                          toggleTextSection(entry.id, "input");
                                        }
                                      }}
                                    >
                                      Input text
                                    </div>
                                    {isInputTextOpen ? (
                                      <pre className="sh_auditlog-json-block sh_auditlog-json-block-old">
                                        <code>{entry.inputText || "(empty)"}</code>
                                      </pre>
                                    ) : null}
                                  </div>
                                  <div className="sh_auditlog-json-section">
                                    <div
                                      className="sh_auditlog-json-label sh_auditlog-json-new"
                                      role="button"
                                      tabIndex={0}
                                      style={{ cursor: "pointer" }}
                                      onClick={() => toggleTextSection(entry.id, "output")}
                                      onKeyDown={(e) => {
                                        if (e.key === "Enter" || e.key === " ") {
                                          e.preventDefault();
                                          toggleTextSection(entry.id, "output");
                                        }
                                      }}
                                    >
                                      Output text
                                    </div>
                                    {isOutputTextOpen ? (
                                      <pre className="sh_auditlog-json-block sh_auditlog-json-block-new">
                                        <code>{entry.outputText || "(empty)"}</code>
                                      </pre>
                                    ) : null}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}

                    <div
                      className={`sh_auditlog-empty ${logEntries.length === 0 ? "" : "d-none"}`}
                    >
                      <div className="text-center py-5">
                        <div className="fw-semibold">
                          {loading
                            ? "Loading AI audit entries..."
                            : "No AI calls found for this visit"}
                        </div>
                        <div className="text-muted small">Try adjusting your filters.</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {paginationNav && (
                <div className="modal-footer flex-shrink-0 p-0 border-top bg-white">
                  <div className="d-flex justify-content-between align-items-center mt-1 sh_cardFooter sh_cardbglight sh_pagination w-100 mb-0 px-3 py-2">
                    <div className="d-flex align-items-center">
                      <span className="text-sm mr-2">Showing</span>
                      <div className="dropdown">
                        <button
                          className="btn bg-white input-group-text mr-2 dropdown-toggle text-sm"
                          type="button"
                          data-bs-toggle="dropdown"
                          disabled={loading}
                        >
                          {perPage}
                        </button>
                        <div className="dropdown-menu text-sm">
                          {PER_PAGE_OPTIONS.map((v) => (
                            <button
                              key={v}
                              type="button"
                              className={`dropdown-item ${perPage === v ? "selected" : ""}`}
                              onClick={() => handlePerPageSelect(v)}
                            >
                              {v}
                            </button>
                          ))}
                        </div>
                      </div>
                      <span className="text-sm">of {totalRecords} items</span>
                    </div>

                    <div className="d-flex align-items-center">
                      <ul
                        className={`sh_paginationbox mb-1 d-flex list-unstyled${
                          loading ? " pe-none opacity-50" : ""
                        }`}
                      >
                        {page !== 1 && (
                          <li className="page-item cursor-pointer">
                            <a className="page-link" onClick={() => handlePageNavigate(1)}>
                              «
                            </a>
                          </li>
                        )}
                        {page !== 1 && (
                          <li className="page-item cursor-pointer">
                            <a className="page-link" onClick={() => handlePageNavigate(page - 1)}>
                              ‹
                            </a>
                          </li>
                        )}
                        {paginationNav.visiblePages.map((pageNum) => (
                          <li
                            key={pageNum}
                            className={`page-item cursor-pointer ${
                              pageNum === page ? "active" : ""
                            }`}
                          >
                            <a
                              className="page-link action_bg"
                              onClick={() => handlePageNavigate(pageNum)}
                            >
                              {pageNum}
                            </a>
                          </li>
                        ))}
                        {page !== paginationNav.totalPages && (
                          <li className="page-item cursor-pointer">
                            <a
                              className="page-link"
                              onClick={() => handlePageNavigate(page + 1)}
                            >
                              ›
                            </a>
                          </li>
                        )}
                        {page !== paginationNav.totalPages && (
                          <li className="page-item cursor-pointer">
                            <a
                              className="page-link"
                              onClick={() => handlePageNavigate(paginationNav.totalPages)}
                            >
                              »
                            </a>
                          </li>
                        )}
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show"></div>
    </>
  );
};

export default VisitAiAuditModal;
